import React, { useEffect, useMemo, useState } from 'react'
import { jsPDF } from 'jspdf'
import autoTable from 'jspdf-autotable'
import { GetAction } from '../../Component/GetAction'
import PaymentReceipt from '../../View/PaymentReceipt'
import ManageTable from '../ManageTable'
import {
  RequireModule,
  PageHeader,
  formatInr,
  downloadCsv,
} from './shared'

function formatDisplayDate(iso) {
  if (!iso) return ''
  const [y, m, d] = String(iso).split('-')
  if (!y || !m || !d) return iso
  return `${d}-${m}-${y}`
}

function rowDate(row) {
  const raw = row?.date_added || row?.created_at || ''
  if (!raw) return null
  const s = String(raw).trim()
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const d = new Date(s.replace(' ', 'T'))
    return Number.isNaN(d.getTime()) ? null : d
  }
  const m = s.match(/^(\d{2})-(\d{2})-(\d{4})(?:\s+(\d{2}):(\d{2}))?/)
  if (m) {
    const d = new Date(
      Number(m[3]),
      Number(m[2]) - 1,
      Number(m[1]),
      Number(m[4] || 0),
      Number(m[5] || 0)
    )
    return Number.isNaN(d.getTime()) ? null : d
  }
  const t = Date.parse(s)
  return Number.isNaN(t) ? null : new Date(t)
}

function startOfDay(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(y, m - 1, d, 0, 0, 0, 0)
}

function endOfDay(iso) {
  const [y, m, d] = String(iso).split('-').map(Number)
  return new Date(y, m - 1, d, 23, 59, 59, 999)
}

function DonationTypeReportPage() {
  const [rows, setRows] = useState([])
  const [types, setTypes] = useState([])
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(true)
  const [receipt, setReceipt] = useState(null)
  const [receiptAutoDownload, setReceiptAutoDownload] = useState(false)
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [donationTypeId, setDonationTypeId] = useState('')

  const openReceipt = (row, autoDownload = false) => {
    setReceiptAutoDownload(!!autoDownload)
    setReceipt(row)
  }

  const closeReceipt = () => {
    setReceipt(null)
    setReceiptAutoDownload(false)
  }

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      setLoading(true)
      const [data, typeData] = await Promise.all([
        GetAction('get', { status: 'paid' }, 'donation/donationList'),
        GetAction('get', {}, 'donation_type/donationTypes'),
      ])
      if (cancelled) return
      if (data?.status === 'success' && Array.isArray(data.response)) {
        setRows(data.response)
        setError('')
      } else {
        setError(data?.message || 'Failed to load donation report.')
        setRows([])
      }
      if (typeData?.status === 'success' && Array.isArray(typeData.response)) {
        setTypes(typeData.response)
      }
      setLoading(false)
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const rangeInvalid = Boolean(fromDate && toDate && fromDate > toDate)

  const filteredRows = useMemo(() => {
    const typeId = String(donationTypeId || '')
    const from = fromDate ? startOfDay(fromDate) : null
    const to = toDate ? endOfDay(toDate) : null

    return rows
      .filter((row) => {
        if (typeId) {
          const rowType = String(row.donation_type_id ?? '')
          if (rowType !== typeId) return false
        }
        if (from || to) {
          const dt = rowDate(row)
          if (!dt) return false
          if (from && dt < from) return false
          if (to && dt > to) return false
        }
        return true
      })
      .map((row, i) => ({ ...row, index: i + 1 }))
  }, [rows, fromDate, toDate, donationTypeId])

  const clearFilters = () => {
    setFromDate('')
    setToDate('')
    setDonationTypeId('')
  }

  const hasFilters = Boolean(fromDate || toDate || donationTypeId)
  const hasDateRange = Boolean(fromDate && toDate && !rangeInvalid)

  const totalAmount = useMemo(
    () =>
      filteredRows.reduce((sum, row) => sum + (Number(row.payment_amount) || 0), 0),
    [filteredRows]
  )

  const selectedTypeName = useMemo(() => {
    if (!donationTypeId) return ''
    const type = types.find((t) => String(t.donation_type_id) === String(donationTypeId))
    return type?.name || ''
  }, [types, donationTypeId])

  const columns = [
    { key: 'index', label: '#', width: '60px' },
    { key: 'name', label: 'Name' },
    {
      key: 'mobile',
      label: 'Mobile',
      render: (row) => row.mobile || '—',
    },
    {
      key: 'type_name',
      label: 'Donation Type',
      render: (row) => row.type_name || row.donation_type_name || '—',
    },
    {
      key: 'payment_amount',
      label: 'Amount',
      render: (row) => formatInr(row.payment_amount),
    },
    { key: 'created_at', label: 'Date' },
    {
      key: 'actions',
      label: 'Actions',
      render: (row) => (
        <div className="d-flex flex-wrap gap-1">
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={() => openReceipt(row, false)}
            title="View receipt"
          >
            View
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => openReceipt(row, true)}
            title="Download PDF receipt"
          >
            <i className="fa fa-file-pdf-o" aria-hidden="true" /> PDF
          </button>
        </div>
      ),
    },
  ]

  const exportRows = () => {
    downloadCsv(
      'donation-type-report.csv',
      [
        { key: 'index', label: '#' },
        { key: 'name', label: 'Name' },
        { key: 'mobile', label: 'Mobile' },
        {
          key: 'type_name',
          label: 'Donation Type',
          get: (r) => r.type_name || r.donation_type_name || '',
        },
        { key: 'payment_amount', label: 'Amount' },
        { key: 'created_at', label: 'Date' },
        { key: 'transaction_id', label: 'Txn' },
      ],
      filteredRows
    )
  }

  const exportPdf = () => {
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' })
    doc.setFontSize(16)
    doc.text('Donation Type Report', 14, 16)
    doc.setFontSize(10)
    let y = 24
    if (hasDateRange) {
      doc.text(
        `Date range: ${formatDisplayDate(fromDate)} to ${formatDisplayDate(toDate)}`,
        14,
        y
      )
      y += 6
    }
    if (selectedTypeName) {
      doc.text(`Donation type: ${selectedTypeName}`, 14, y)
      y += 6
    }
    doc.text(`Records: ${filteredRows.length}`, 14, y)
    y += 4

    autoTable(doc, {
      startY: y + 2,
      head: [['#', 'Name', 'Mobile', 'Donation Type', 'Amount', 'Date']],
      body: filteredRows.map((row) => [
        row.index,
        row.name || '',
        row.mobile || '',
        row.type_name || row.donation_type_name || '',
        formatInr(row.payment_amount),
        row.created_at || '',
      ]),
      foot: [['', '', '', 'Total amount', formatInr(totalAmount), '']],
      theme: 'striped',
      styles: { fontSize: 9, cellPadding: 2 },
      headStyles: { fillColor: [253, 126, 20], textColor: 255, fontStyle: 'bold' },
      footStyles: {
        fillColor: [255, 248, 240],
        textColor: [196, 90, 0],
        fontStyle: 'bold',
      },
      columnStyles: {
        0: { cellWidth: 12 },
        4: { halign: 'right' },
      },
    })

    const fromPart = fromDate || 'start'
    const toPart = toDate || 'end'
    doc.save(`donation-type-report-${fromPart}-to-${toPart}.pdf`)
  }

  return (
    <RequireModule moduleKey="donation_type_reports">
      <PageHeader
        title="Donation Type Report"
        subtitle="Paid donations — filter by date range and donation type, then view or download PDF receipts."
      />
      {error ? <div className="manage-alert manage-alert-err">{error}</div> : null}

      <div className="manage-card mb-3">
        <div className="manage-filters">
          <div className="row align-items-end">
            <div className="col-md-3 mb-2">
              <label className="form-label" htmlFor="dtr_from">
                From date
              </label>
              <input
                id="dtr_from"
                type="date"
                className="form-control"
                value={fromDate}
                max={toDate || undefined}
                onChange={(e) => setFromDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 mb-2">
              <label className="form-label" htmlFor="dtr_to">
                To date
              </label>
              <input
                id="dtr_to"
                type="date"
                className="form-control"
                value={toDate}
                min={fromDate || undefined}
                onChange={(e) => setToDate(e.target.value)}
              />
            </div>
            <div className="col-md-4 mb-2">
              <label className="form-label" htmlFor="dtr_type">
                Donation type
              </label>
              <select
                id="dtr_type"
                className="form-control"
                value={donationTypeId}
                onChange={(e) => setDonationTypeId(e.target.value)}
              >
                <option value="">All types</option>
                {types.map((t) => (
                  <option key={t.donation_type_id} value={String(t.donation_type_id)}>
                    {t.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-2 mb-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-block w-100"
                onClick={clearFilters}
                disabled={!hasFilters}
              >
                Clear
              </button>
            </div>
          </div>
          {rangeInvalid ? (
            <div className="text-danger small mt-1">From date cannot be after To date.</div>
          ) : null}
        </div>
      </div>

      <div className="manage-card">
        {loading ? (
          <div className="text-center py-4">
            <div className="spinner-border text-warning" role="status" />
          </div>
        ) : (
          <ManageTable
            columns={columns}
            rows={rangeInvalid ? [] : filteredRows}
            hideSearch
            searchKeys={[
              'name',
              'mobile',
              'type_name',
              'donation_type_name',
              'payment_amount',
              'created_at',
            ]}
            emptyMessage={
              hasFilters
                ? 'No paid donations match the selected date range and type.'
                : 'No paid donations found.'
            }
            footer={(visible) => {
              if (!hasDateRange || !visible.length) return null
              const visibleTotal = visible.reduce(
                (sum, row) => sum + (Number(row.payment_amount) || 0),
                0
              )
              return (
                <tr>
                  <td colSpan={4} className="text-right text-end">
                    Total amount
                  </td>
                  <td>{formatInr(visibleTotal)}</td>
                  <td colSpan={2} />
                </tr>
              )
            }}
          />
        )}
        {!loading ? (
          <div className="manage-export-bar">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              onClick={exportRows}
              disabled={!filteredRows.length || rangeInvalid}
            >
              Export CSV
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger"
              onClick={exportPdf}
              disabled={!hasDateRange || !filteredRows.length}
              title="Download filtered table as PDF"
            >
              <i className="fa fa-file-pdf-o" aria-hidden="true" /> Export PDF
            </button>
          </div>
        ) : null}
      </div>
      {receipt ? (
        <PaymentReceipt
          hide={closeReceipt}
          title="Donation History"
          data={receipt}
          autoDownload={receiptAutoDownload}
        />
      ) : null}
    </RequireModule>
  )
}

export default DonationTypeReportPage
